import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  BookOpen,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Eye,
  Filter,
  Layers3,
  Pencil,
  Plus,
  RefreshCcw,
  Ruler,
  Search,
  Tags,
  Trash2,
  UtensilsCrossed,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  categoriesApi,
  menusApi,
  productsApi,
} from '../../api';
import { useAlerts } from '../../components/feedback/AlertProvider';
import { useConfirm } from '../../components/feedback/ConfirmProvider';
import { useLanguage } from '../../contexts/LanguageContext';
import getTranslatedField from '../../i18n/helpers/getTranslatedField';
import translateApiError from '../../utils/translateApiError';
import resolveMediaUrl from '../../utils/resolveMediaUrl';
import '../../styles/Products/products.css';

const DEFAULT_ESTABLISHMENT_ID = 1;
const SELECTED_MENU_STORAGE_KEY = 'selected_menu_id';
const PRODUCTS_PER_PAGE = 10;

const translateFallback = (t, key, fallback) => {
  const value = t(key);

  return value === key ? fallback : value;
};

const formatNumber = (value, language) => {
  const number = Number(value || 0);

  try {
    return new Intl.NumberFormat(language).format(number);
  } catch {
    return String(number);
  }
};


function CustomSelect({
  value,
  options = [],
  onChange,
  disabled = false,
  placeholder = '—',
  ariaLabel,
  className = '',
  portal = false,
}) {
  const rootRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [portalStyle, setPortalStyle] = useState(null);

  const selectedOption =
    options.find(
      (option) =>
        String(option.value) ===
        String(value),
    ) || null;

  // Position table dropdowns outside the table while preserving
  // the exact same visual structure as the Menus dropdown.
  const updatePortalPosition = () => {
    if (!portal || !rootRef.current) {
      return;
    }

    const rect =
      rootRef.current.getBoundingClientRect();

    const direction =
      window.getComputedStyle(rootRef.current)
        .direction ||
      document.documentElement.dir ||
      'ltr';

    const viewportPadding = 8;
    const gap = 8;
    const estimatedMenuHeight = Math.min(
      options.length * 38 +
        Math.max(options.length - 1, 0) * 4 +
        12,
      240,
    );

    const spaceBelow =
      window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    const openAbove =
      spaceBelow < estimatedMenuHeight + gap &&
      spaceAbove > spaceBelow;

    let top = openAbove
      ? rect.top - estimatedMenuHeight - gap
      : rect.bottom + gap;

    top = Math.max(
      viewportPadding,
      Math.min(
        top,
        window.innerHeight -
          estimatedMenuHeight -
          viewportPadding,
      ),
    );

    const width = rect.width;

    let left =
      direction === 'rtl'
        ? rect.right - width
        : rect.left;

    left = Math.max(
      viewportPadding,
      Math.min(
        left,
        window.innerWidth -
          width -
          viewportPadding,
      ),
    );

    setPortalStyle({
      top,
      left,
      width,
      direction,
      placement: openAbove
        ? 'above'
        : 'below',
    });
  };

  useEffect(() => {
    const handleOutsideClick = (event) => {
      const clickedTrigger =
        rootRef.current?.contains(event.target);

      const clickedPortal =
        menuRef.current?.contains(event.target);

      if (!clickedTrigger && !clickedPortal) {
        setOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener(
      'mousedown',
      handleOutsideClick,
    );
    document.addEventListener(
      'keydown',
      handleEscape,
    );

    return () => {
      document.removeEventListener(
        'mousedown',
        handleOutsideClick,
      );
      document.removeEventListener(
        'keydown',
        handleEscape,
      );
    };
  }, []);

  useEffect(() => {
    if (!open || !portal) {
      setPortalStyle(null);
      return undefined;
    }

    updatePortalPosition();

    const frame = window.requestAnimationFrame(
      updatePortalPosition,
    );

    window.addEventListener(
      'resize',
      updatePortalPosition,
    );
    window.addEventListener(
      'scroll',
      updatePortalPosition,
      true,
    );

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener(
        'resize',
        updatePortalPosition,
      );
      window.removeEventListener(
        'scroll',
        updatePortalPosition,
        true,
      );
    };
    // Reposition when the menu opens or its size can change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, portal, options.length]);

  const renderOptions = () =>
    options.map((option) => {
      const active =
        String(option.value) ===
        String(value);

      return (
        <button
          key={option.value}
          type="button"
          role="option"
          aria-selected={active}
          className={
            active
              ? 'is-active'
              : ''
          }
          onClick={() => {
            onChange?.(
              option.value,
            );

            setOpen(false);
          }}
        >
          <span>
            {option.label}
          </span>

          {active ? (
            <Check size={15} />
          ) : null}
        </button>
      );
    });

  const portalMenu =
    open &&
    portal &&
    portalStyle
      ? createPortal(
          <div
            ref={menuRef}
            className={`products-custom-select__menu products-custom-select__menu--portal ${
              portalStyle.placement === 'above'
                ? 'is-above'
                : 'is-below'
            }`}
            role="listbox"
            dir={portalStyle.direction}
            style={{
              top: `${portalStyle.top}px`,
              left: `${portalStyle.left}px`,
              width: `${portalStyle.width}px`,
            }}
          >
            {renderOptions()}
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      <div
        ref={rootRef}
        className={`products-custom-select ${className}`}
      >
        <button
          type="button"
          className={`products-custom-select__trigger ${
            open ? 'is-open' : ''
          }`}
          onClick={() => {
            if (!disabled) {
              if (!open && portal) {
                updatePortalPosition();
              }

              setOpen(
                (current) =>
                  !current,
              );
            }
          }}
          disabled={disabled}
          aria-label={ariaLabel}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <span>
            {selectedOption?.label ||
              placeholder}
          </span>

          <ChevronDown
            size={16}
            className={
              open
                ? 'is-rotated'
                : ''
            }
          />
        </button>

        {open && !portal ? (
          <div
            className="products-custom-select__menu"
            role="listbox"
          >
            {renderOptions()}
          </div>
        ) : null}
      </div>

      {portalMenu}
    </>
  );
}

function Products() {
  const { t, language } = useLanguage();
  const { showAlert } = useAlerts();
  const { confirm } = useConfirm();

  const direction = String(language || '')
    .toLowerCase()
    .startsWith('ar')
    ? 'rtl'
    : 'ltr';

  const isRtl = direction === 'rtl';

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [menus, setMenus] = useState([]);
  const [meta, setMeta] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const [selectedMenuId, setSelectedMenuId] = useState(
    localStorage.getItem(SELECTED_MENU_STORAGE_KEY) || '',
  );

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedPricingType, setSelectedPricingType] = useState('');

  const [loading, setLoading] = useState(true);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [menusLoading, setMenusLoading] = useState(true);
  const [error, setError] = useState('');

  const establishmentId =
    localStorage.getItem('selected_establishment_id') ||
    DEFAULT_ESTABLISHMENT_ID;

  const selectedMenu = useMemo(
    () =>
      menus.find(
        (menu) => String(menu.id) === String(selectedMenuId),
      ) || null,
    [menus, selectedMenuId],
  );

  const filters = useMemo(
    () => ({
      menu_id: selectedMenuId || undefined,
      search: searchTerm || undefined,
      status: selectedStatus || undefined,
      category_id: selectedCategory || undefined,
      pricing_type: selectedPricingType || undefined,
      locale: language,
      page: currentPage,
      per_page: PRODUCTS_PER_PAGE,
    }),
    [
      selectedMenuId,
      searchTerm,
      selectedStatus,
      selectedCategory,
      selectedPricingType,
      language,
      currentPage,
    ],
  );

  const productsWithSizesCount = products.filter(
    (product) => product.pricing_type === 'sizes',
  ).length;

  // Format the translated category label.
  const formatCategoryLabel = (category) => {
    const categoryName =
      getTranslatedField(
        category,
        'name',
        language,
        'fr',
      ) || t('categories.noName');

    const parentName = getTranslatedField(
      category.parent,
      'name',
      language,
      'fr',
    );

    if (category.parent_id) {
      return `${parentName || t(
        'categories.details.mainCategory',
      )} / ${categoryName}`;
    }

    return categoryName;
  };

  // Format product prices.
  const formatPrice = (value) => {
    if (
      value === null ||
      value === undefined ||
      value === ''
    ) {
      return '-';
    }

    return `${Number(value).toFixed(2)} MAD`;
  };

  // Resolve the displayed price.
  const getProductPrice = (product) => {
    if (product.pricing_type === 'sizes') {
      const sizes = product.sizes || [];

      const defaultSize =
        sizes.find((size) => size.is_default) ||
        sizes[0] ||
        null;

      if (!defaultSize) {
        return t('products.pricing.noPrice');
      }

      return `≥ ${formatPrice(
        defaultSize.discount_price ||
          defaultSize.price,
      )}`;
    }

    return formatPrice(
      product.discount_price ||
        product.base_price,
    );
  };

  // Load establishment menus.
  const fetchMenus = async () => {
    try {
      setMenusLoading(true);

      const response = await menusApi.getMenus(
        establishmentId,
        {
          per_page: 100,
          locale: language,
        },
      );

      const responseMenus =
        response.data?.data?.menus ||
        response.data?.data ||
        [];

      const normalizedMenus = Array.isArray(
        responseMenus,
      )
        ? responseMenus
        : [];

      setMenus(normalizedMenus);

      const storedMenuId =
        localStorage.getItem(
          SELECTED_MENU_STORAGE_KEY,
        );

      const storedMenuExists =
        normalizedMenus.some(
          (menu) =>
            String(menu.id) ===
            String(storedMenuId),
        );

      if (storedMenuExists) {
        setSelectedMenuId(
          String(storedMenuId),
        );

        return;
      }

      const defaultMenu =
        normalizedMenus.find(
          (menu) =>
            Boolean(menu.is_default),
        ) ||
        normalizedMenus.find(
          (menu) =>
            Boolean(menu.is_active),
        ) ||
        normalizedMenus[0];

      if (defaultMenu?.id) {
        const menuId =
          String(defaultMenu.id);

        localStorage.setItem(
          SELECTED_MENU_STORAGE_KEY,
          menuId,
        );

        setSelectedMenuId(menuId);
      } else {
        localStorage.removeItem(
          SELECTED_MENU_STORAGE_KEY,
        );

        setSelectedMenuId('');
      }
    } catch (apiError) {
      const translatedError =
        translateApiError(
          apiError,
          t,
        );

      setError(translatedError);

      showAlert({
        type: 'error',
        title: t('alerts.error'),
        message: translatedError,
      });
    } finally {
      setMenusLoading(false);
    }
  };

  // Load products from the selected menu.
  const fetchProducts = async () => {
    if (!selectedMenuId) {
      setProducts([]);
      setMeta(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError('');

      const response =
        await productsApi.getProducts(
          establishmentId,
          filters,
        );

      setProducts(
        response.data?.data || [],
      );

      setMeta(
        response.data?.meta || null,
      );
    } catch (apiError) {
      const translatedError =
        translateApiError(
          apiError,
          t,
        );

      setError(translatedError);

      showAlert({
        type: 'error',
        title: t('alerts.error'),
        message: translatedError,
      });
    } finally {
      setLoading(false);
    }
  };

  // Load categories from the selected menu.
  const fetchCategories = async () => {
    if (!selectedMenuId) {
      setCategories([]);
      setCategoriesLoading(false);
      return;
    }

    try {
      setCategoriesLoading(true);

      const response =
        await categoriesApi.getActiveCategories(
          establishmentId,
          {
            menu_id: selectedMenuId,
            locale: language,
          },
        );

      setCategories(
        response.data?.data || [],
      );
    } catch {
      setCategories([]);
    } finally {
      setCategoriesLoading(false);
    }
  };

  useEffect(() => {
    fetchMenus();
  }, [establishmentId, language]);

  useEffect(() => {
    setSelectedCategory('');

    fetchCategories();
  }, [selectedMenuId, language]);

  useEffect(() => {
    const timeout =
      setTimeout(() => {
        fetchProducts();
      }, 350);

    return () =>
      clearTimeout(timeout);
  }, [filters]);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    searchTerm,
    selectedStatus,
    selectedCategory,
    selectedPricingType,
  ]);

  useEffect(() => {
    const lastPage = Number(meta?.last_page || 1);

    if (currentPage > lastPage) {
      setCurrentPage(lastPage);
    }
  }, [meta?.last_page, currentPage]);

  // Switch active menu.
  const handleMenuChange = (menuId) => {

    setSelectedMenuId(menuId);

    setSearchTerm('');
    setSelectedStatus('');
    setSelectedCategory('');
    setSelectedPricingType('');
    setCurrentPage(1);

    if (menuId) {
      localStorage.setItem(
        SELECTED_MENU_STORAGE_KEY,
        menuId,
      );
    } else {
      localStorage.removeItem(
        SELECTED_MENU_STORAGE_KEY,
      );
    }
  };

  // Delete a product using the global confirmation dialog.
  const handleDelete = async (
    product,
  ) => {
    const productName =
      getTranslatedField(
        product,
        'name',
        language,
        'fr',
      ) ||
      t('products.noName');

    const confirmed = await confirm({
      title: t(
        'products.actions.delete',
      ),
      message: t(
        'products.confirmDelete',
      ),
      itemName: productName,
      confirmText: t(
        'products.actions.delete',
      ),
      cancelText: t(
        'common.cancel',
      ),
      closeText: t(
        'common.close',
      ),
      variant: 'danger',
    });

    if (!confirmed) {
      return;
    }

    try {
      await productsApi.deleteProduct(
        product.id,
      );

      showAlert({
        type: 'success',
        title: t('alerts.success'),
        message: t(
          'products.alerts.deleted',
        ),
      });

      await fetchProducts();
    } catch (apiError) {
      const translatedError =
        translateApiError(
          apiError,
          t,
        );

      setError(translatedError);

      showAlert({
        type: 'error',
        title: t('alerts.error'),
        message: translatedError,
      });
    }
  };

  // Update product availability.
  const handleAvailabilityChange =
    async (
      productId,
      status,
    ) => {
      try {
        await productsApi.updateAvailability(
          productId,
          status,
        );

        showAlert({
          type: 'success',
          title: t(
            'alerts.success',
          ),
          message: t(
            'products.alerts.availabilityUpdated',
          ),
        });

        await fetchProducts();
      } catch (apiError) {
        const translatedError =
          translateApiError(
            apiError,
            t,
          );

        setError(
          translatedError,
        );

        showAlert({
          type: 'error',
          title: t(
            'alerts.error',
          ),
          message:
            translatedError,
        });
      }
    };

  const totalPages = Math.max(
    1,
    Number(meta?.last_page || 1),
  );

  const activePage = Math.min(
    Math.max(1, Number(meta?.current_page || currentPage)),
    totalPages,
  );

  const paginationStart = Number(meta?.from || 0);
  const paginationEnd = Number(meta?.to || 0);
  const paginationTotal = Number(meta?.total || products.length);

  const pageNumbers = useMemo(() => {
    const maxVisiblePages = 5;

    const firstPage = Math.max(
      1,
      Math.min(
        activePage - 2,
        totalPages - maxVisiblePages + 1,
      ),
    );

    const lastPage = Math.min(
      totalPages,
      firstPage + maxVisiblePages - 1,
    );

    return Array.from(
      {
        length: Math.max(
          0,
          lastPage - firstPage + 1,
        ),
      },
      (_, index) => firstPage + index,
    );
  }, [activePage, totalPages]);


  return (
    <div className="products-page" dir={direction}>
      <section className="products-heading">
        <div>
          <p className="products-heading__eyebrow">
            {t('products.eyebrow')}
          </p>

          <h1>
            {t('products.title')}
          </h1>

          <p>
            {t(
              'products.description',
            )}
          </p>
        </div>

        <Link
          to="/products/new"
          className="dashboard-primary-button"
        >
          <Plus size={18} />

          {t(
            'products.addProduct',
          )}
        </Link>
      </section>

      <section className="products-menu-context">
        <div className="products-menu-context__icon">
          <BookOpen size={22} />
        </div>

        <div className="products-menu-context__content">
          <span>
            {t('menus.title')}
          </span>

          <strong>
            {getTranslatedField(
              selectedMenu,
              'name',
              language,
              'fr',
            ) || '—'}
          </strong>

          {getTranslatedField(
            selectedMenu,
            'description',
            language,
            'fr',
          ) ? (
            <small>
              {getTranslatedField(
                selectedMenu,
                'description',
                language,
                'fr',
              )}
            </small>
          ) : null}
        </div>

        <CustomSelect
          value={selectedMenuId}
          onChange={handleMenuChange}
          disabled={menusLoading}
          ariaLabel={t('menus.title')}
          className="products-menu-context__select"
          placeholder={
            menusLoading
              ? t(
                  'menus.loading',
                )
              : t(
                  'menus.empty.title',
                )
          }
          options={menus.map(
            (menu) => ({
              value:
                String(
                  menu.id,
                ),
              label:
                 `${getTranslatedField(
                   menu,
                   'name',
                   language,
                   'fr',
                 ) || '—'}${
                   menu.is_default
                     ? ` • ${t(
                         'menus.status.default',
                       )}`
                     : ''
                 }`,
            }),
          )}
        />
      </section>

      <section className="products-summary-grid">
        <article className="products-summary-card">
          <span>
            <UtensilsCrossed
              size={20}
            />
          </span>

          <div>
            <strong>
              {formatNumber(
                meta?.total ||
                  products.length,
                language,
              )}
            </strong>

            <p>
              {t(
                'products.summary.total',
              )}
            </p>
          </div>
        </article>

        <article className="products-summary-card">
          <span>
            <Tags size={20} />
          </span>

          <div>
            <strong>
              {formatNumber(
                categories.length,
                language,
              )}
            </strong>

            <p>
              {t(
                'products.summary.categories',
              )}
            </p>
          </div>
        </article>

        <article className="products-summary-card">
          <span>
            <Ruler size={20} />
          </span>

          <div>
            <strong>
              {formatNumber(
                productsWithSizesCount,
                language,
              )}
            </strong>

            <p>
              {t(
                'products.summary.withSizes',
              )}
            </p>
          </div>
        </article>

        <article className="products-summary-card">
          <span>
            <Filter size={20} />
          </span>

          <div>
            <strong>
              {formatNumber(
                products.length,
                language,
              )}
            </strong>

            <p>
              {t(
                'products.summary.filtered',
              )}
            </p>
          </div>
        </article>
      </section>

      <section className="products-panel">
        <div className="products-toolbar">
          <div className="products-search">
            <Search size={18} />

            <input
              type="search"
              value={
                searchTerm
              }
              onChange={(
                event,
              ) =>
                setSearchTerm(
                  event.target
                    .value,
                )
              }
              placeholder={t(
                'products.searchPlaceholder',
              )}
              disabled={
                !selectedMenuId
              }
            />
          </div>

          <CustomSelect
            value={selectedCategory}
            onChange={setSelectedCategory}
            className="products-filter"
            disabled={
              categoriesLoading ||
              !selectedMenuId
            }
            ariaLabel={t(
              'products.filters.allCategories',
            )}
            options={[
              {
                value: '',
                label: t(
                  'products.filters.allCategories',
                ),
              },

              ...categories.map(
                (category) => ({
                  value:
                    String(
                      category.id,
                    ),
                  label:
                    formatCategoryLabel(
                      category,
                    ),
                }),
              ),
            ]}
          />

          <CustomSelect
            value={selectedPricingType}
            onChange={setSelectedPricingType}
            className="products-filter"
            disabled={!selectedMenuId}
            ariaLabel={t(
              'products.filters.allPricingTypes',
            )}
            options={[
              {
                value: '',
                label: t(
                  'products.filters.allPricingTypes',
                ),
              },
              {
                value: 'fixed',
                label: t(
                  'products.pricing.fixed',
                ),
              },
              {
                value: 'sizes',
                label: t(
                  'products.pricing.sizes',
                ),
              },
            ]}
          />

          <CustomSelect
            value={selectedStatus}
            onChange={setSelectedStatus}
            className="products-filter"
            disabled={!selectedMenuId}
            ariaLabel={t(
              'products.filters.allStatuses',
            )}
            options={[
              {
                value: '',
                label: t(
                  'products.filters.allStatuses',
                ),
              },
              {
                value:
                  'available',
                label: t(
                  'products.status.available',
                ),
              },
              {
                value:
                  'sold_out',
                label: t(
                  'products.status.sold_out',
                ),
              },
              {
                value:
                  'hidden',
                label: t(
                  'products.status.hidden',
                ),
              },
            ]}
          />
        </div>

        {error ? (
          <div className="products-alert products-alert--error">
            {error}
          </div>
        ) : null}

        {!selectedMenuId &&
        !menusLoading ? (
          <div className="products-empty-state">
            <BookOpen
              size={34}
            />

            <h2>
              {t(
                'menus.empty.title',
              )}
            </h2>

            <p>
              {t(
                'menus.empty.description',
              )}
            </p>

            <Link
              to="/menus/new"
              className="dashboard-primary-button"
            >
              <Plus
                size={18}
              />

              {t(
                'menus.addMenu',
              )}
            </Link>
          </div>
        ) : loading ? (
          <div className="products-empty-state">
            <RefreshCcw
              size={34}
              className="menus-loading-icon"
            />

            <h2>
              {t(
                'products.loading',
              )}
            </h2>
          </div>
        ) : products.length >
          0 ? (
          <div className="products-table-wrapper">
            <table className="products-table">
              <thead>
                <tr>
                  <th>
                    {t(
                      'products.table.product',
                    )}
                  </th>

                  <th>
                    {t(
                      'products.table.category',
                    )}
                  </th>

                  <th>
                    {t(
                      'products.table.pricing',
                    )}
                  </th>

                  <th>
                    {t(
                      'products.table.price',
                    )}
                  </th>

                  <th>
                    {t(
                      'products.table.status',
                    )}
                  </th>

                  <th>
                    {t(
                      'products.table.actions',
                    )}
                  </th>
                </tr>
              </thead>

              <tbody>
                {products.map(
                  (product) => {
                    const productName =
                      getTranslatedField(
                        product,
                        'name',
                        language,
                        'fr',
                      );

                    const productDescription =
                      getTranslatedField(
                        product,
                        'description',
                        language,
                        'fr',
                      );

                    const categoryName =
                      getTranslatedField(
                        product.category,
                        'name',
                        language,
                        'fr',
                      );

                    const parentCategoryName =
                      getTranslatedField(
                        product.category
                          ?.parent,
                        'name',
                        language,
                        'fr',
                      );

                    const primaryImageUrl =
                      resolveMediaUrl(
                        product
                          .primary_image
                          ?.url ||
                          product.image_url,
                      );

                    return (
                      <tr
                        key={
                          product.id
                        }
                      >
                        <td>
                          <div className="products-table__product">
                            <span className="products-table__image">
                              {primaryImageUrl ? (
                                <img
                                  src={
                                    primaryImageUrl
                                  }
                                  alt={
                                    productName
                                  }
                                />
                              ) : (
                                <UtensilsCrossed
                                  size={
                                    18
                                  }
                                />
                              )}
                            </span>

                            <div>
                              <strong>
                                {productName ||
                                  t(
                                    'products.noName',
                                  )}
                              </strong>

                              <small>
                                {productDescription ||
                                  t(
                                    'products.noDescription',
                                  )}
                              </small>
                            </div>
                          </div>
                        </td>

                        <td>
                          {parentCategoryName
                            ? `${parentCategoryName} / ${categoryName}`
                            : categoryName ||
                              '-'}
                        </td>

                        <td>
                          <span className="products-pricing-type">
                            <Layers3
                              size={
                                14
                              }
                            />

                            {product.pricing_type ===
                            'sizes'
                              ? t(
                                  'products.pricing.sizes',
                                )
                              : t(
                                  'products.pricing.fixed',
                                )}
                          </span>
                        </td>

                        <td>
                          <div className="products-table__price">
                            {product.pricing_type ===
                              'fixed' &&
                            product.discount_price ? (
                              <span>
                                {formatPrice(
                                  product.base_price,
                                )}
                              </span>
                            ) : null}

                            <strong>
                              {getProductPrice(
                                product,
                              )}
                            </strong>
                          </div>
                        </td>

                        <td>
                          <CustomSelect
                            value={
                              product.status
                            }
                            onChange={(
                              value,
                            ) =>
                              handleAvailabilityChange(
                                product.id,
                                value,
                              )
                            }
                            className="product-status-select"
                            portal
                            ariaLabel={t(
                              'products.table.status',
                            )}
                            options={[
                              {
                                value:
                                  'available',
                                label: t(
                                  'products.status.available',
                                ),
                              },
                              {
                                value:
                                  'sold_out',
                                label: t(
                                  'products.status.sold_out',
                                ),
                              },
                              {
                                value:
                                  'hidden',
                                label: t(
                                  'products.status.hidden',
                                ),
                              },
                            ]}
                          />
                        </td>

                        <td>
                          <div className="products-table__actions">
                            <Link
                              to={`/products/${product.id}`}
                              aria-label={t(
                                'products.actions.preview',
                              )}
                            >
                              <Eye
                                size={
                                  17
                                }
                              />
                            </Link>

                            <Link
                              to={`/products/${product.id}/edit`}
                              aria-label={t(
                                'products.actions.edit',
                              )}
                            >
                              <Pencil
                                size={
                                  17
                                }
                              />
                            </Link>

                            <button
                              type="button"
                              className="products-table__delete"
                              onClick={() =>
                                handleDelete(
                                  product,
                                )
                              }
                              aria-label={t(
                                'products.actions.delete',
                              )}
                            >
                              <Trash2
                                size={
                                  17
                                }
                              />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  },
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="products-empty-state">
            <UtensilsCrossed
              size={34}
            />

            <h2>
              {t(
                'products.empty.title',
              )}
            </h2>

            <p>
              {t(
                'products.empty.description',
              )}
            </p>
          </div>
        )}

        {selectedMenuId &&
        !loading &&
        paginationTotal > 0 ? (
          <div className="products-pagination">
            <div className="products-pagination__summary">
              <span>
                {translateFallback(
                  t,
                  'common.showing',
                  'Showing',
                )}
              </span>

              <strong>
                {formatNumber(paginationStart, language)} - {formatNumber(paginationEnd, language)}
              </strong>

              <span>
                {translateFallback(
                  t,
                  'common.of',
                  'of',
                )}
              </span>

              <strong>{formatNumber(paginationTotal, language)}</strong>
            </div>

            <div className="products-pagination__controls">
              <button
                type="button"
                className="products-pagination__button"
                disabled={activePage <= 1}
                onClick={() =>
                  setCurrentPage((page) =>
                    Math.max(1, page - 1),
                  )
                }
              >
                {isRtl ? (
                  <ChevronRight size={17} />
                ) : (
                  <ChevronLeft size={17} />
                )}

                {translateFallback(
                  t,
                  'common.previous',
                  'Previous',
                )}
              </button>

              <div className="products-pagination__pages">
                {pageNumbers.map((pageNumber) => (
                  <button
                    key={pageNumber}
                    type="button"
                    className={
                      activePage === pageNumber
                        ? 'active'
                        : ''
                    }
                    onClick={() =>
                      setCurrentPage(pageNumber)
                    }
                  >
                    {formatNumber(pageNumber, language)}
                  </button>
                ))}
              </div>

              <button
                type="button"
                className="products-pagination__button"
                disabled={activePage >= totalPages}
                onClick={() =>
                  setCurrentPage((page) =>
                    Math.min(
                      totalPages,
                      page + 1,
                    ),
                  )
                }
              >
                {translateFallback(
                  t,
                  'common.next',
                  'Next',
                )}

                {isRtl ? (
                  <ChevronLeft size={17} />
                ) : (
                  <ChevronRight size={17} />
                )}
              </button>
            </div>
          </div>
        ) : null}
      </section>
    </div>
  );
}

export default Products;